import {
  Button,
  Card,
  CardBody,
  FormGroup,
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Row,
  Col,
  Spinner,
  Badge,
} from "reactstrap";

import { ChangeEvent, useContext, useMemo, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import { UserProps } from "../context/hooks/useAuth";

const Login = () => {
  const { handleLogin } = useContext(AuthContext);

  const [login, setLogin] = useState<UserProps>({
    password: "",
    email: "",
  });

  const [loading, setLoading] = useState(false);

  const handleLoginClick = async () => {
    setLoading(true);
    try {
      await handleLogin(login);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setLogin((prev) => ({ ...prev, [name]: value }));
  };

  const isDisabled = useMemo(() => {
    return loading || !login.email?.trim() || !login.password?.trim();
  }, [loading, login.email, login.password]);

  return (
    <Col lg="11" xl="10" className="mx-auto">
      <Card
        className="shadow-lg border-0 overflow-hidden"
        style={{
          borderRadius: 22,
          background: "linear-gradient(135deg, #f6f8fb 0%, #eef2f7 100%)",
        }}
      >
        <Row className="no-gutters">
          <Col
            lg="6"
            className="p-4 d-flex flex-column justify-content-between"
            style={{
              background:
                "linear-gradient(135deg, rgba(108,117,125,0.10) 0%, rgba(108,117,125,0.02) 60%, rgba(255,255,255,0) 100%)",
              borderRight: "1px solid rgba(0,0,0,0.06)",
              minHeight: 430,
            }}
          >
            <div>
              <div className="d-flex justify-content-center">
                <img
                  style={{ width: "54%", maxWidth: 240 }}
                  alt="Logo"
                  className="d-block mb-3"
                  src={require("../assets/img/brand/logoAtt.png")}
                />
              </div>

              <Badge
                pill
                className="mb-3"
                style={{
                  backgroundColor: "rgba(108,117,125,0.12)",
                  color: "#495057",
                  fontWeight: 600,
                }}
              >
                Air App
              </Badge>

              <h2 style={{ fontWeight: 800, marginBottom: 8, color: "#212529" }}>
                Bem-vindo
              </h2>

              <p className="text-muted" style={{ fontSize: 14, lineHeight: 1.55, marginBottom: 10 }}>
                O <strong>AirApp</strong> ajuda você a gerenciar sua carteira de seguros,
                acompanhar renovações, leads e visitas de forma integrada — para aumentar
                a produtividade da sua equipe.
              </p>

              <div className="mt-3">
                {[
                  {
                    icon: "ni ni-chart-bar-32",
                    title: "Visão rápida",
                    desc: "Acompanhe o que está vencendo e o que precisa de ação agora.",
                  },
                  {
                    icon: "ni ni-bell-55",
                    title: "Renovações",
                    desc: "Menos esquecimentos, mais controle do seu funil.",
                  },
                  {
                    icon: "ni ni-single-copy-04",
                    title: "Organização",
                    desc: "Tudo centralizado: leads, visitas, carteira e histórico.",
                  },
                ].map((item) => (
                  <div className="d-flex align-items-start mb-2" key={item.title}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 12,
                        backgroundColor: "rgba(108,117,125,0.12)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: 12,
                        flexShrink: 0,
                      }}
                    >
                      <i className={item.icon} style={{ color: "#6c757d" }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, color: "#343a40", fontSize: 14 }}>
                        {item.title}
                      </div>
                      <div className="text-muted" style={{ fontSize: 13, lineHeight: 1.35 }}>
                        {item.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-3">
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="small"
                style={{ color: "#6c757d", textDecoration: "underline" }}
              >
                Política de Privacidade
              </a>
              <div className="text-muted small mt-2">
                Ao continuar, você concorda com o uso responsável da plataforma.
              </div>
            </div>
          </Col>

          <Col lg="6" className="p-4 d-flex align-items-center">
            <div style={{ width: "100%" }}>
              <Card
                className="border-0 shadow-sm"
                style={{
                  borderRadius: 18,
                  backgroundColor: "#ffffff",
                }}
              >
                <CardBody className="p-4">
                  <div className="mb-3">
                    <div style={{ fontWeight: 800, fontSize: 18, color: "#212529" }}>
                      Acessar sua conta
                    </div>
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      Informe seus dados para entrar no portal.
                    </div>
                  </div>

                  <Form role="form" onSubmit={(e) => e.preventDefault()}>
                    <FormGroup className="mb-3">
                      <div className="small text-muted mb-2">E-mail</div>
                      <InputGroup className="input-group-alternative">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText
                            style={{
                              borderTopLeftRadius: 12,
                              borderBottomLeftRadius: 12,
                              backgroundColor: "#f8f9fa",
                            }}
                          >
                            <i className="ni ni-email-83" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          name="email"
                          placeholder="seuemail@dominio.com"
                          type="email"
                          autoComplete="username"
                          value={login.email}
                          onChange={handleInputChange}
                          style={{
                            backgroundColor: "#fff",
                            borderTopRightRadius: 12,
                            borderBottomRightRadius: 12,
                            height: 44,
                          }}
                        />
                      </InputGroup>
                    </FormGroup>

                    <FormGroup className="mb-2">
                      <div className="small text-muted mb-2">Senha</div>
                      <InputGroup className="input-group-alternative">
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText
                            style={{
                              borderTopLeftRadius: 12,
                              borderBottomLeftRadius: 12,
                              backgroundColor: "#f8f9fa",
                            }}
                          >
                            <i className="ni ni-lock-circle-open" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          name="password"
                          placeholder="Digite sua senha"
                          type="password"
                          autoComplete="current-password"
                          value={login.password}
                          onChange={handleInputChange}
                          style={{
                            backgroundColor: "#fff",
                            borderTopRightRadius: 12,
                            borderBottomRightRadius: 12,
                            height: 44,
                          }}
                        />
                      </InputGroup>
                    </FormGroup>

                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="custom-control custom-control-alternative custom-checkbox m-0">
                        <input
                          className="custom-control-input"
                          id="customCheckLogin"
                          type="checkbox"
                        />
                        <label className="custom-control-label" htmlFor="customCheckLogin">
                          <span className="text-muted">Lembre-me</span>
                        </label>
                      </div>

                      <a
                        className="small"
                        href="#pablo"
                        onClick={(e) => {
                          e.preventDefault();
                          window.location.href = "/auth/forgot-password";
                        }}
                        style={{ color: "#6c757d", textDecoration: "underline" }}
                      >
                        Esqueceu a senha?
                      </a>
                    </div>

                    <Button
                      onClick={handleLoginClick}
                      className="w-100"
                      color="secondary"
                      type="button"
                      disabled={isDisabled}
                      style={{
                        backgroundColor: "#6c757d",
                        color: "#FFF",
                        borderRadius: 14,
                        height: 44,
                        fontWeight: 700,
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" /> <span className="ml-2">Entrando...</span>
                        </>
                      ) : (
                        "Entrar"
                      )}
                    </Button>

                    <div className="text-muted small text-center mt-3">
                      Suporte e acesso: fale com o administrador da sua empresa.
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>
      </Card>
    </Col>
  );
};

export default Login;